'use client'

import { useState, useRef } from 'react'
import { TrendingUp, Upload, ChevronDown } from 'lucide-react'
import api from '@/lib/platform-api'
import { formatFastApiDetail } from '@/lib/format-fastapi-detail'
import { useNotification } from '@/context/NotificationContext'
import { useRequireModule } from '@/hooks/useRequireModule'

interface PredictionData {
  success: boolean
  total_products: number
  week_starting?: string
  predictions?: Array<{ product: string; predicted_quantity: number; confidence?: number }>
  message?: string
}

export default function PredictPage() {
  const { ready } = useRequireModule('module_product_requirements')
  const { addNotification } = useNotification()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [columns, setColumns] = useState<string[]>([])
  const [dateCol, setDateCol] = useState('')
  const [productCol, setProductCol] = useState('')
  const [qtyCol, setQtyCol] = useState('')
  const [isDrag, setIsDrag] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [predictions, setPredictions] = useState<PredictionData | null>(null)

  const parseColumns = (f: File): Promise<string[]> => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => {
      const text = e.target?.result as string
      const lines = text.split('\n')
      if (lines.length > 0) resolve(lines[0].split(',').map(c => c.trim().replace(/^"|"$/g, '')))
      else reject(new Error('Empty CSV'))
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(f)
  })

  const handleFile = async (f: File) => {
    if (!f.name.endsWith('.csv')) { addNotification({ type: 'error', title: 'Error', message: 'Please upload a CSV file' }); return }
    setFile(f); setPredictions(null)
    try {
      const cols = await parseColumns(f)
      setColumns(cols)
      setDateCol(cols.find(c => ['date','time','week','day','period','timestamp'].some(k => c.toLowerCase().includes(k))) || '')
      setProductCol(cols.find(c => ['product','item','sku','name'].some(k => c.toLowerCase().includes(k))) || '')
      setQtyCol(cols.find(c => ['quantity','qty','amount','requirement','demand','sales','units'].some(k => c.toLowerCase().includes(k))) || '')
    } catch (err: unknown) {
      const e = err as { message?: string }
      addNotification({ type: 'error', title: 'Parse Error', message: e.message || 'Failed to parse CSV' })
      setFile(null)
    }
  }

  const handlePredict = async () => {
    if (!file) { addNotification({ type: 'error', title: 'Error', message: 'Please select a CSV file' }); return }
    if (!dateCol || !productCol || !qtyCol) { addNotification({ type: 'error', title: 'Error', message: 'Please select all required columns' }); return }
    setProcessing(true); setPredictions(null)
    try {
      const fd = new FormData()
      fd.append('f', file)
      fd.append('date_column', dateCol)
      fd.append('product_column', productCol)
      fd.append('quantity_column', qtyCol)
      const res = await api.post('/predict-product-requirements/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (res.data.success) {
        setPredictions(res.data)
        addNotification({ type: 'success', title: 'Done!', message: `Predicted requirements for ${res.data.total_products} products` })
      } else {
        addNotification({ type: 'error', title: 'Error', message: res.data.message || 'Prediction failed' })
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
      addNotification({
        type: 'error',
        title: 'Error',
        message: formatFastApiDetail(e.response?.data?.detail, 'Prediction failed'),
      })
    } finally { setProcessing(false) }
  }

  const SelectCol = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <div>
      <label className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)}
          className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-700 focus:border-violet-400 focus:ring-4 focus:ring-violet-400/10 outline-none cursor-pointer pr-10">
          <option value="">— Select column —</option>
          {columns.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>
    </div>
  )

  if (!ready) return null

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 text-white mb-4 shadow-lg shadow-violet-500/30">
            <TrendingUp size={28} />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-violet-500 to-purple-700 bg-clip-text text-transparent mb-2">Requirements Prediction</h1>
          <p className="text-slate-500 text-lg">Forecast next week&apos;s product needs from order history using XGBoost</p>
        </div>

        {/* Upload */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6">
          <h2 className="text-lg font-semibold text-slate-700 mb-4">1. Upload Sales CSV</h2>
          <div
            className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${isDrag ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:border-violet-300 hover:bg-slate-50'}`}
            onDragOver={e => { e.preventDefault(); setIsDrag(true) }}
            onDragLeave={() => setIsDrag(false)}
            onDrop={e => { e.preventDefault(); setIsDrag(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
            onClick={() => fileInputRef.current?.click()}>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
            <Upload size={36} className={`mx-auto mb-3 ${isDrag ? 'text-violet-400' : 'text-slate-300'}`} />
            {file ? (
              <><p className="font-semibold text-slate-700">{file.name}</p><p className="text-sm text-slate-400 mt-1">Click to replace</p></>
            ) : (
              <><p className="font-medium text-slate-500">Drag &amp; drop CSV here or click to browse</p><p className="text-sm text-slate-400 mt-1">Needs date, product, and quantity columns</p></>
            )}
          </div>
        </div>

        {/* Column mapping */}
        {columns.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-slate-700 mb-4">2. Map Columns</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SelectCol label="Date Column *" value={dateCol} onChange={setDateCol} />
              <SelectCol label="Product Column *" value={productCol} onChange={setProductCol} />
              <SelectCol label="Quantity Column *" value={qtyCol} onChange={setQtyCol} />
            </div>
          </div>
        )}

        {/* Predict button */}
        {file && (
          <button onClick={handlePredict} disabled={processing || !dateCol || !productCol || !qtyCol}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-700 text-white font-bold text-lg shadow-lg shadow-violet-500/25 hover:-translate-y-0.5 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mb-8">
            {processing ? 'Predicting…' : 'Predict Next Week Requirements'}
          </button>
        )}

        {/* Results */}
        {predictions && predictions.predictions && predictions.predictions.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-700">Predictions</h2>
              <span className="text-sm text-slate-400">{predictions.total_products} products{predictions.week_starting && ` • Week of ${predictions.week_starting}`}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Product</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Predicted Qty</th>
                    {predictions.predictions[0]?.confidence !== undefined && (
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Confidence</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {predictions.predictions.map((p, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-medium text-slate-700">{p.product}</td>
                      <td className="py-3 px-4 text-right">
                        <span className="inline-flex items-center justify-center px-3 py-1 rounded-lg bg-violet-50 text-violet-700 font-bold">
                          {p.predicted_quantity.toLocaleString()}
                        </span>
                      </td>
                      {p.confidence !== undefined && (
                        <td className="py-3 px-4 text-right text-slate-500">{(p.confidence * 100).toFixed(1)}%</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
